/**
 * Message Strategy Service
 *
 * Frontend service for message strategy operations (claims, framings, angles).
 */

import type { ICampaignAngle, IMessageClaim, ITargetFraming } from '@/types/campaigns'
import { createApiRequest } from '../api/createApiRequest'
import { API_TIMEOUTS } from '@/config/api-timeouts'

interface GenerateClaimsRequest {
    generation_notes?: string
    run_id?: string
    targets?: Array<{
        name: string
        profile: any
        tfd_goals?: {
            think?: string
            feel?: string
            do?: string
        }
    }>
}
void (null as any as GenerateClaimsRequest)

interface GenerateClaimsResponse {
    success: boolean
    claims: IMessageClaim[]
}
void (null as any as GenerateClaimsResponse)

interface UpdateClaimsRequest {
    claims: IMessageClaim[]
}
void (null as any as UpdateClaimsRequest)

interface UpdateClaimsResponse {
    success: boolean
    claims: IMessageClaim[]
}
void (null as any as UpdateClaimsResponse)

interface GenerateFramingRequest {
    target: {
        name: string
        profile: any
        tfd_goals?: {
            think?: string
            feel?: string
            do?: string
        }
    }
}
void (null as any as GenerateFramingRequest)

interface GenerateFramingResponse {
    success: boolean
    framing: ITargetFraming
}
void (null as any as GenerateFramingResponse)

interface SetCampaignAngleRequest {
    angle_text: string
}
void (null as any as SetCampaignAngleRequest)

interface SetCampaignAngleResponse {
    success: boolean
    angle: ICampaignAngle
}
void (null as any as SetCampaignAngleResponse)

interface ListAllClaimsResponse {
    claims: Array<
        IMessageClaim & {
            source_profile_type: 'company' | 'product' | 'person'
            source_profile_id: string
            source_profile_name: string
        }
    >
    count: number
}
void (null as any as ListAllClaimsResponse)

/**
 * List all claims across all profiles
 */
export async function listAllClaims(): Promise<ListAllClaimsResponse> {
    const data = await createApiRequest<ListAllClaimsResponse>({
        url: `/campaigns/library/claims`,
        method: 'GET',
    })

    return data
}

/**
 * Generate Claims from profile assets
 */
export async function generateClaims(
    profileType: 'product' | 'company' | 'person',
    profileId: string,
    request: GenerateClaimsRequest
): Promise<GenerateClaimsResponse> {
    const url = `/campaigns/library/${profileType}/${profileId}/message-strategy/generate-claims`
    console.log('[messageStrategyService] Calling generateClaims:', {
        url,
        profileType,
        profileId,
        request,
    })

    const data = await createApiRequest<GenerateClaimsResponse>({
        url,
        method: 'POST',
        data: request as any,
        timeout: API_TIMEOUTS.AI_GENERATION,
    })

    console.log('[messageStrategyService] generateClaims response:', data)
    return data
}

/**
 * Generate Strategic Angles Only (Phase 1 of split flow)
 * Returns angles immediately (~90s). Then queue claim generation for each angle.
 */
export async function generateAngles(
    profileType: 'product' | 'company' | 'person',
    profileId: string,
    request: GenerateClaimsRequest
): Promise<{
    success: boolean
    strategic_angles: any[]
    assets: any[]
    subject_context: any
    run_context: any
}> {
    const url = `/campaigns/library/${profileType}/${profileId}/message-strategy/generate-angles`
    
    const data = await createApiRequest<any>({
        url,
        method: 'POST',
        data: request as any,
        timeout: API_TIMEOUTS.EXTENDED_AI, // Angle extraction with quality validation retries can take 3-4 min
    })

    return data
}

/**
 * Queue Claim Generation for Single Angle (Phase 2 of split flow)
 * Returns jobId immediately. Listen for WebSocket events for completion.
 */
export async function queueClaimGeneration(
    profileType: 'product' | 'company' | 'person',
    profileId: string,
    request: {
        angle: any
        assets: any[]
        subject_context?: any
        run_context?: any
        generation_notes?: string
    }
): Promise<{
    success: boolean
    jobId: string
    angle_id: string
    angle_name: string
}> {
    const url = `/campaigns/library/${profileType}/${profileId}/message-strategy/queue-claim-generation`
    
    const data = await createApiRequest<any>({
        url,
        method: 'POST',
        data: request as any,
        timeout: API_TIMEOUTS.QUICK_OPERATION, // Just queues the job, should be fast
    })

    return data
}

/**
 * Update Claims (edit, priority, included)
 */
export async function updateClaims(
    profileType: 'product' | 'company' | 'person',
    profileId: string,
    request: UpdateClaimsRequest
): Promise<UpdateClaimsResponse> {
    const data = await createApiRequest<UpdateClaimsResponse>({
        url: `/campaigns/library/${profileType}/${profileId}/message-strategy/claims`,
        method: 'PATCH',
        data: request as any,
    })

    return data
}

/**
 * Get Claims for a specific profile
 */
export async function getClaims(
    profileType: 'product' | 'company' | 'person',
    profileId: string
): Promise<{ claims: IMessageClaim[] }> {
    const data = await createApiRequest<{ claims: IMessageClaim[] }>({
        url: `/campaigns/library/${profileType}/${profileId}/message-strategy/claims`,
        method: 'GET',
    })

    return data
}

/**
 * Generate target-specific framing for a claim
 */
export async function generateFraming(
    profileType: 'product' | 'company' | 'person',
    profileId: string,
    claimId: string,
    request: GenerateFramingRequest
): Promise<GenerateFramingResponse> {
    const data = await createApiRequest<GenerateFramingResponse>({
        url: `/campaigns/library/${profileType}/${profileId}/message-strategy/claims/${claimId}/framing`,
        method: 'POST',
        data: request as any,
    })

    return data
}

/**
 * Set campaign angle
 */
export async function setCampaignAngle(
    profileType: 'product' | 'company' | 'person',
    profileId: string,
    request: SetCampaignAngleRequest
): Promise<SetCampaignAngleResponse> {
    const data = await createApiRequest<SetCampaignAngleResponse>({
        url: `/campaigns/library/${profileType}/${profileId}/message-strategy/campaign-angle`,
        method: 'PATCH',
        data: request as any,
    })

    return data
}

/**
 * Get primary claim
 */
export async function getPrimaryClaim(
    profileType: 'product' | 'company' | 'person',
    profileId: string
): Promise<{ success: boolean; claim: IMessageClaim }> {
    const data = await createApiRequest<{ success: boolean; claim: IMessageClaim }>({
        url: `/campaigns/library/${profileType}/${profileId}/message-strategy/primary-claim`,
        method: 'GET',
    })

    return data
}

/**
 * Generate primary claim rationale
 */
export async function generatePrimaryRationale(
    profileType: 'product' | 'company' | 'person',
    profileId: string,
    claimId: string
): Promise<{ success: boolean; rationale: string }> {
    const data = await createApiRequest<{ success: boolean; rationale: string }>({
        url: `/campaigns/library/${profileType}/${profileId}/message-strategy/claims/${claimId}/generate-rationale`,
        method: 'POST',
    })

    return data
}

/**
 * Message Strategy service object
 */
export const messageStrategyService = {
    listAllClaims,
    generateClaims,
    generateAngles,
    queueClaimGeneration,
    updateClaims,
    getClaims,
    generateFraming,
    setCampaignAngle,
    getPrimaryClaim,
    generatePrimaryRationale,
}
